import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/pages/home/components/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie: Movie | undefined = getMovieById(id || '');

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-500">
                <i className="ri-error-warning-line text-2xl"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
                未找到该影片
              </h1>
              <p className="text-foreground-600 mb-6">抱歉，您要查看的影片不存在或已下架</p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.actors.map((a) => ({ '@type': 'Person', name: a })),
    dateCreated: `${movie.year}`,
    url: `https://www.tianlangcinema.com/detail/${movie.id}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div
            className="relative w-full h-[300px] md:h-[400px] bg-cover bg-center"
            style={{ backgroundImage: `url(${movie.bannerImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background-50"></div>
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-32 relative z-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: movie.category, href: `/category/${movie.category === '电影' ? 'movie' : movie.category === '电视剧' ? 'tv' : movie.category === '综艺' ? 'variety' : movie.category === '动漫' ? 'anime' : 'documentary'}` },
                { label: movie.title },
              ]}
            />

            <div className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="w-44 md:w-56 flex-shrink-0 mx-auto md:mx-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden">
                  <img
                    src={movie.coverImage}
                    alt={movie.title}
                    title={`${movie.title} - 天狼影院高清在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-1">{movie.englishTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="flex items-center gap-1 text-accent-500 text-sm font-medium">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-xs"></i>
                    </div>
                    {movie.rating}
                  </span>
                  <span className="text-sm text-foreground-500">{movie.year}</span>
                  <span className="text-sm text-foreground-500">{movie.region}</span>
                  <span className="text-sm text-foreground-500">{movie.duration}</span>
                  {movie.isNew && (
                    <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded whitespace-nowrap">
                      新上线
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs rounded whitespace-nowrap">
                      热播中
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full border border-secondary-200 whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 mb-6">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-fill"></i>
                    </div>
                    立即播放
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full border border-background-300 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full border border-background-300 hover:bg-background-200 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-share-forward-line"></i>
                    </div>
                    分享
                  </button>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-1">导演</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.director}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-1">主演</p>
                    <p className="text-sm font-medium text-foreground-900 line-clamp-1">
                      {movie.actors.join('、')}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-background-100">
                    <p className="text-xs text-foreground-500 mb-1">地区</p>
                    <p className="text-sm font-medium text-foreground-900">{movie.region}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4">
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-4xl">
                {movie.longDescription}
              </p>
            </div>

            {relatedMovies.length > 0 && (
              <div className="mb-14">
                <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}